import moment from "moment";
import { formatMomentPtBr } from "../../utils/dateRange";
import { Button } from "@rio-cloud/rio-uikit";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch } from "../../store";
import { setEndDate, setStartDate } from "@store/date/dateSlice";
import Card from "@rio-cloud/rio-uikit/Card";

interface MonthNavigationProps {
  startDate?: string;
  endDate?: string;
}

export default function SmallMonthlyNavigation({
  startDate,
  endDate,
}: MonthNavigationProps) {
  const dispatch = useAppDispatch();
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => new Date());

  const getDisplayText = () => {
    if (startDate && endDate) {
      const start = moment(startDate, "YYYY-MM-DD");
      const end = moment(endDate, "YYYY-MM-DD");

      const isStartOfMonth = start.date() === 1;
      const isEndOfMonth = end.date() === end.daysInMonth();
      const isSameMonth = start.month() === end.month() && start.year() === end.year();

      if (isStartOfMonth && isEndOfMonth && isSameMonth) {
        const monthName = formatMomentPtBr(start, "MMMM").toUpperCase();
        return <span className="text-size-h2 text-bold text-color-darkest margin-0">{monthName} {start.format("YYYY")}</span>;
      }

      const formattedStart = start.format("DD/MM/YYYY");
      const formattedEnd = end.format("DD/MM/YYYY");
      return <span className="text-size-h4 text-bold text-color-darkest margin-0">{`Período: de ${formattedStart} até ${formattedEnd}`}</span> ;
    }

    return <span className="text-size-h2 text-bold text-color-darkest margin-0">{currentMonth}
    </span>;
  };

  const goToPrevMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().subtract(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const goToNextMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().add(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const currentMonth = useMemo(
    () => selectedMonth.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).toUpperCase(),
    [selectedMonth]
  );

  return (
    <Card className='flex-1-1  padding-20 height-100pct'>
      <div className="width-100pct display-flex flex-column justify-content-center align-items-center">
        <div className="display-flex align-items-center gap-2">
          <span className='rioglyph rioglyph-calendar text-size-14' />
          <p className="text-size-14 text-color-darkest margin-0">Período</p>
        </div>
        <div
          className='display-flex justify-content-center align-items-center gap-20 width-100pct'
        >
          <Button
            variant={Button.VARIANT_LINK}
            iconOnly
            iconName="rioglyph-triangle-left"
            onClick={(e) => {
              e?.stopPropagation();
              goToPrevMonth();
            }}
          />

          {getDisplayText()}

          <Button
            iconOnly
            variant={Button.VARIANT_LINK}
            iconName="rioglyph-triangle-right"
            onClick={(e) => {
              e?.stopPropagation();
              goToNextMonth();
            }}
          />
        </div>
      </div>
    </Card>
  );
}
